import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Heart,
  Shield,
  Users,
  Sparkles,
  DollarSign,
  CreditCard,
  Rocket,
  Globe,
  Server,
  Zap,
} from "lucide-react";
import { useDonation } from "@/hooks/useDonation";
import { toast } from "@/hooks/use-toast";
import donateHeroImage from "@/assets/donate-hero.png";

// ✅ LIVE PayPal Client ID from user's account
const PAYPAL_CLIENT_ID = "AZhTvr-H1ZT74t5JfqO7-P04fFQ4KRHanreP4Ycm8aQEV3siPcaHU7SiF2fI_-rWaWWYzmBrEoq8pm2_";

const PRESET_AMOUNTS = [10, 25, 50, 100, 200];

// 🎯 Inner component that uses PayPal hook - must be inside PayPalScriptProvider
const DonateContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordDonation } = useDonation();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(25);
  const [customAmount, setCustomAmount] = useState("");

  // ✅ Use PayPal's official hook for state management
  const [{ isPending, isResolved, isRejected }] = usePayPalScriptReducer();

  // 🐛 DEBUG: Log PayPal and donation states
  console.log("💳 PayPal State:", { isPending, isResolved, isRejected });
  console.log("💰 Donation State:", { selectedAmount, customAmount, amount: customAmount ? parseFloat(customAmount) : selectedAmount });

  const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const handleApprove = async (data: any, actions: any) => {
    try {
      const details = await actions.order.capture();
      const transactionId = details.id;
      const paidAmount = parseFloat(details.purchase_units[0].amount.value);

      await recordDonation.mutateAsync({
        transaction_id: transactionId,
        amount: paidAmount,
        currency: "USD",
      });

      navigate(`/donation-success?amount=${paidAmount}&ref=${transactionId}`);
    } catch (error) {
      console.error("Error processing donation:", error);
      toast({
        title: t("common.error"),
        description: t("donate.error"),
        variant: "destructive",
      });
    }
  };

  const supports = [
    {
      icon: <Server className="h-6 w-6" />,
      title: t("donate.supports.item1"),
      description: t("donate.supports.desc1"),
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: t("donate.supports.item2"),
      description: t("donate.supports.desc2"),
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: t("donate.supports.item3"),
      description: t("donate.supports.desc3"),
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: t("donate.supports.item4"),
      description: t("donate.supports.desc4"),
    },
  ];

  return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>

          {/* Split Hero Section - 50/50 Layout */}
          <div className="mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl bg-card">
              {/* Left Side - Image (50%) */}
              <div className="relative lg:sticky lg:top-0 h-[400px] lg:h-[600px] bg-gradient-to-br from-muted/20 to-muted/5 flex items-center justify-center p-8 lg:p-12">
                <div className="bg-white p-3 rounded-lg shadow-2xl">
                  <img 
                    src={donateHeroImage} 
                    alt="Petius Community - Support Our Mission" 
                    className="w-full h-full max-h-[340px] lg:max-h-[540px] object-contain rounded"
                  />
                </div>
              </div>

              {/* Right Side - Emotional Content (50%) */}
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center bg-gradient-to-br from-background via-background to-muted/20">
                {/* Main Headline */}
                <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 mb-6">
                <Globe className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-semibold text-purple-700">{t("donate.hero.badge")}</span>
              </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                    {t("donate.hero.headline")}
                  </h1>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                    {t("donate.hero.subheadline")}
                  </p>
                </div>

                {/* Benefits List with Icons */}
                <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Rocket className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t("donate.hero.benefit1Title")}</h3>
                    <p className="text-sm text-muted-foreground">{t("donate.hero.benefit1Desc")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Globe className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t("donate.hero.benefit2Title")}</h3>
                    <p className="text-sm text-muted-foreground">{t("donate.hero.benefit2Desc")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Heart className="h-5 w-5 text-white fill-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t("donate.hero.benefit3Title")}</h3>
                    <p className="text-sm text-muted-foreground">{t("donate.hero.benefit3Desc")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t("donate.hero.benefit4Title")}</h3>
                    <p className="text-sm text-muted-foreground">{t("donate.hero.benefit4Desc")}</p>
                  </div>
                </div>
                </div>

                {/* Impact Stats */}
                <div className="grid grid-cols-2 gap-4 mb-8 p-6 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">{t("donate.hero.stat1")}</div>
                    <div className="text-xs text-muted-foreground">Global Reach</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">{t("donate.hero.stat2")}</div>
                    <div className="text-xs text-muted-foreground">Infrastructure</div>
                  </div>
                </div>

                {/* Trust Signals */}
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span>{t("donate.hero.trust1")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span>{t("donate.hero.trust2")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-purple-600" />
                    <span>{t("donate.hero.trust3")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 🔄 PayPal Loading State */}
          {isPending && (
            <Alert className="mb-6">
              <AlertDescription>
                {t("donate.loading") || "Loading PayPal..."}
              </AlertDescription>
            </Alert>
          )}

          {/* ❌ PayPal Error State */}
          {isRejected && (
            <Alert className="mb-6 border-destructive/50 text-destructive">
              <AlertDescription>
                <div className="space-y-3">
                  <p className="font-semibold">
                    ⚠️ {t("donate.paypalError") || "PayPal could not be loaded"}
                  </p>
                  <p className="text-sm">
                    {t("donate.paypalErrorDesc") || "Please check your internet connection and try refreshing the page. If the problem persists, contact support."}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => window.location.reload()}
                    className="mt-2"
                  >
                    🔄 {t("common.refresh") || "Refresh Page"}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Donation Amount Selection - Enhanced */}
          <Card className="mb-8 border-2 border-pink-100 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-pink-50 to-purple-50">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-6 w-6 text-pink-600 fill-pink-600" />
                <CardTitle className="text-2xl">{t("donate.selectAmount")}</CardTitle>
              </div>
              <CardDescription className="text-base">{t("donate.selectAmountDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Preset Amounts */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {PRESET_AMOUNTS.map((preset) => (
                  <Button
                    key={preset}
                    variant={selectedAmount === preset && !customAmount ? "default" : "outline"}
                    onClick={() => {
                      console.log("🔘 Donation button clicked:", preset);
                      setSelectedAmount(preset);
                      setCustomAmount("");
                    }}
                    className={`h-24 flex flex-col transition-all duration-200 ${
                      selectedAmount === preset && !customAmount 
                        ? "bg-gradient-to-br from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 shadow-lg scale-105" 
                        : "hover:border-pink-300 hover:bg-pink-50"
                    }`}
                  >
                    <DollarSign className="h-6 w-6 mb-1" />
                    <span className="text-3xl font-bold">{preset}</span>
                  </Button>
                ))}
              </div>

              {/* Custom Amount */}
              <div>
                <Label htmlFor="customAmount">{t("donate.customAmount")}</Label>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-2xl font-bold">$</span>
                  <Input
                    id="customAmount"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder={t("donate.amountPlaceholder")}
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="text-lg"
                  />
                </div>
              </div>

              {/* PayPal Buttons - Only show if SDK loaded successfully */}
              {isResolved && !isPending && amount && amount >= 1 && (
                <div className="pt-4 max-w-md mx-auto">
                  <PayPalButtons
                    style={{ layout: "vertical", color: "blue", shape: "rect", label: "donate" }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        intent: "CAPTURE",
                        purchase_units: [
                          {
                            amount: {
                              currency_code: "USD",
                              value: amount.toFixed(2),
                            },
                            description: "Donation to Petius",
                          },
                        ],
                      });
                    }}
                    onApprove={handleApprove}
                    onError={(err) => {
                      console.error("PayPal Error:", err);
                      toast({
                        title: t("common.error"),
                        description: t("donate.error"),
                        variant: "destructive",
                      });
                    }}
                  />
                </div>
              )}

              {/* Messages based on state */}
              {isResolved && (!amount || amount < 1) && (
                <p className="text-sm text-muted-foreground text-center">
                  💡 {t("donate.minAmount") || "Please select an amount of at least $1"}
                </p>
              )}

              {!isResolved && !isPending && !isRejected && (
                <p className="text-sm text-muted-foreground text-center">
                  ⏳ {t("donate.paypalInitializing") || "Initializing payment system..."}
                </p>
              )}
            </CardContent>
          </Card>

          {/* What Your Donation Supports */}
          <Card>
            <CardHeader>
              <CardTitle>{t("donate.supports.title")}</CardTitle>
              <CardDescription>{t("donate.supports.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {supports.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/10 to-purple-500/10 flex items-center justify-center text-purple-600">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Security Notice */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>{t("donate.securePayment")}</span>
            </div>
          </div>
        </div>
      </div>
  );
};

// 🎁 Wrapper component that provides PayPal context
const Donate = () => {
  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        currency: "USD",
      }}
    >
      <DonateContent />
    </PayPalScriptProvider>
  );
};

export default Donate;
